import React from 'react';
import I18n from 'i18n-js';

import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import HeaderDropdown from '@reverbdotcom/commons/src/components/header_dropdown';
import PriceDisplay from '@reverbdotcom/commons/src/components/price_display';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';

import {
  buildSellingSection,
  buildBuyingSection,
  buildFinancesSection,
  buildMyAccountSection,
} from './reverb_header_menu_data';
import baseUrlHelpers from '../../../shared/url_helpers';
import ReverbHeaderMenuSection from './reverb_header_menu_section';
import { ReverbHeaderUserMenuData } from '../../../schemas/rql';

interface IProps {
  me: ReverbHeaderUserMenuData.Fragment;
  activeDropdownKey: string | null;
  setActiveDropdownKey: (string) => void;
}

export const DEFAULT_AVATAR = 'https://reverb-dev-res.cloudinary.com/image/upload/s--BtEvm2y3--/v1546540852/default--rounded_misa1r.png';

function renderReverbCreditsCallouts(reverbCreditsBalances) {
  if (!reverbCreditsBalances?.length) { return null; }

  return (
    <>
      <div className="reverb-header__menu__user-menu__header">
        {I18n.t('discovery.reverbHeader.userMenu.reverbCredits')}
      </div>
      <ul>
        {reverbCreditsBalances.map(({ display, currency }) => (
          <li className="reverb-header__menu__credits-balance" key={`reverb_credits_${currency}`}>
            <PriceDisplay
              display={display}
              currency={currency}
            />
          </li>
        ))}
      </ul>
    </>
  );
}

function renderPayoutBalancesCallouts(payoutBalances) {
  if (!payoutBalances?.length) { return null; }

  return (
    <>
      <div className="reverb-header__menu__user-menu__header">
        {I18n.t('discovery.reverbHeader.userMenu.payoutBalance')}
      </div>
      <ul>
        {payoutBalances.map(({ display, currency }) => (
          <li key={`payout_balances_${currency}`}>
            <CoreLink to={baseUrlHelpers.dashboardEarningsReverbBankTransactionsPath}>
              <span className="weight-bold size-90 color-blue">
                <PriceDisplay
                  display={display}
                  currency={currency}
                />
              </span>
            </CoreLink>
          </li>
        ))}
      </ul>
    </>
  );
}

export function ReverbHeaderDesktopMenu({
  me,
  setActiveDropdownKey,
  activeDropdownKey,
}: IProps) {
  const user = useUser();
  const { name, loggedOut, avatarUrl, locale } = user;
  const { shop, reverbCredits, payoutBalances } = me || {};
  const { slug: shopSlug, paymentMethod: shopPaymentMethod } = shop || {};

  const menuSections = [
    buildSellingSection(user, shopSlug, true),
    buildBuyingSection(),
    shopPaymentMethod && buildFinancesSection(shopPaymentMethod),
    !loggedOut && buildMyAccountSection({ locale }),
  ].filter(Boolean);

  const menu = (
    <div className="reverb-header__menu__user-menu">
      <div className=" bdb-1 bd-primary padding-1 pt-0 weight-bold">
        {name}
      </div>
      {renderPayoutBalancesCallouts(payoutBalances)}
      {renderReverbCreditsCallouts(reverbCredits)}
      {menuSections.map(({ key, title, links }) => (<ReverbHeaderMenuSection key={key} title={title} links={links} />))}
    </div>
  );

  const trigger = (
    <div
      className="reverb-header__nav__link reverb-header__nav__link--user-menu"
    >
      <div className="reverb-header__nav__link__icon reverb-header__nav__link--avatar">
        <img src={avatarUrl || DEFAULT_AVATAR} alt={I18n.t('discovery.reverbHeader.userMenu.userAvatarAlt')} />
      </div>
      <div className="reverb-header__nav__link__label">
        {I18n.t('discovery.reverbHeader.userMenu.badge')}
      </div>
    </div>
  );

  return (
    <HeaderDropdown
      trigger={trigger}
      contents={menu}
      dropdownKey="user_menu"
      onOpen={key => setActiveDropdownKey(key)}
      activeDropdownKey={activeDropdownKey}
    />
  );
}

export default ReverbHeaderDesktopMenu;
